import {reactive, computed, watch} from 'vue';

export default function Account(data = {}) {
    // Reactive state for account fields
    const state = reactive({
        id: data.id || '',
        companyName: data.companyName || '',
        accountCode: data.accountCode || '',
        status: data.status || 'ACTIVE',
        billingCycle: data.billingCycle || 'MONTHLY',
        billingEmail: data.billingEmail || '',
        billingAddress: data.billingAddress || '',
        creditLimit: data.creditLimit || 0,
        paymentTermsDays: data.paymentTermsDays || 30,
        contactPersonName: data.contactPersonName || '',
        contactPersonEmail: data.contactPersonEmail || '',
        contactPersonPhone: data.contactPersonPhone || '',
        notes: data.notes || '',
        autoInvoice: data.autoInvoice || false,
        autoSendInvoice: data.autoSendInvoice || false,
        taxRate: data.taxRate || 0,
        startDate: data.startDate || new Date().toISOString().split('T')[0],
        billingAnchorDate: data.billingAnchorDate || new Date().toISOString().split('T')[0],
    });

    // Computed properties for validations
    const validations = {
        isCompanyNameValid: computed(() => !!state.companyName.trim()),
        isBillingEmailValid: computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.billingEmail)),
        isBillingCycleValid: computed(() => !!state.billingCycle),
        isContactEmailValid: computed(() => !state.contactPersonEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.contactPersonEmail)),
        isContactPhoneValid: computed(() => !state.contactPersonPhone || /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(state.contactPersonPhone))
    };

    // Individual field validation functions
    const validateCompanyName = () => {
        if (validations.isCompanyNameValid.value) {
            $('#account-company-name-input').removeClass('is-invalid');
            $('#account-company-name-error').addClass('d-none');
            return true;
        } else {
            $('#account-company-name-input').addClass('is-invalid');
            $('#account-company-name-error').removeClass('d-none');
            return false;
        }
    };

    const validateBillingEmail = () => {
        if (validations.isBillingEmailValid.value) {
            $('#account-billing-email-input').removeClass('is-invalid');
            $('#account-billing-email-error').addClass('d-none');
            return true;
        } else {
            $('#account-billing-email-input').addClass('is-invalid');
            $('#account-billing-email-error').removeClass('d-none');
            return false;
        }
    };

    const validateContactEmail = () => {
        if (validations.isContactEmailValid.value) {
            $('#account-contact-email-input').removeClass('is-invalid');
            $('#account-contact-email-error').addClass('d-none');
            return true;
        } else {
            $('#account-contact-email-input').addClass('is-invalid');
            $('#account-contact-email-error').removeClass('d-none');
            return false;
        }
    };

    const validateContactPhone = () => {
        if (validations.isContactPhoneValid.value) {
            $('#account-contact-phone-input').removeClass('is-invalid');
            $('#account-contact-phone-error').addClass('d-none');
            return true;
        } else {
            $('#account-contact-phone-input').addClass('is-invalid');
            $('#account-contact-phone-error').removeClass('d-none');
            return false;
        }
    };

    // Set up watchers for real-time validation
    watch(() => state.companyName, validateCompanyName);
    watch(() => state.billingEmail, validateBillingEmail);
    watch(() => state.contactPersonEmail, validateContactEmail);
    watch(() => state.contactPersonPhone, validateContactPhone);

    // Validation logic for the entire form
    const isValid = () => {
        let isCompanyNameValid = validateCompanyName();
        let isBillingEmailValid = validateBillingEmail();
        let isContactEmailValid = validateContactEmail();
        let isContactPhoneValid = validateContactPhone();

        return isCompanyNameValid && isBillingEmailValid && isContactEmailValid && isContactPhoneValid;
    };

    const clearFields = () => {
        state.companyName = '';
        state.accountCode = '';
        state.status = 'ACTIVE';
        state.billingCycle = 'MONTHLY';
        state.billingEmail = '';
        state.billingAddress = '';
        state.creditLimit = 0;
        state.paymentTermsDays = 30;
        state.contactPersonName = '';
        state.contactPersonEmail = '';
        state.contactPersonPhone = '';
        state.notes = '';
        state.autoInvoice = false;
        state.autoSendInvoice = false;
        state.taxRate = 0;
        state.startDate = new Date().toISOString().split('T')[0];
        state.billingAnchorDate = new Date().toISOString().split('T')[0];
    };

    // Initialize validation
    const initializeValidation = () => {
        // Only validate fields that have values
        if (state.companyName) validateCompanyName();
        if (state.billingEmail) validateBillingEmail();
        if (state.contactPersonEmail) validateContactEmail();
        if (state.contactPersonPhone) validateContactPhone();
    };

    // Call initialization after a short delay to ensure DOM is ready
    setTimeout(initializeValidation, 100);

    return {
        state,
        validations,
        isValid,
        clearFields,
        initializeValidation
    };
}
